import { FC } from 'react';
import { Box, BoxProps, Button, Flex, Text, Skeleton, Image, Badge } from '@chakra-ui/react';
import { ICON_BY_NAME } from '../Icons/Icon';
import { formatNumber } from 'ui/helpers';
import ArcadeToken from '../Icons/ArcadeToken';
import { useRewards } from 'providers/RewardsProvider';
import { RewardCard } from 'ui/types/RewardCard';
import { useFavoriteRewards } from 'hooks/useRewards/useFavoriteRewards';
export interface CbRewardCardProps extends Partial<Omit<BoxProps, 'id'>> {
  reward?: RewardCard;
  isPending: boolean;
}
export const CbRewardCard: FC<CbRewardCardProps> = ({
  reward,
  isPending,
  ...props
}) => {
  const {
    handleRewardClick,
    handleFavoriteClick
  } = useRewards();
  const {
    favoritedItems
  } = useFavoriteRewards();
  const hasDiscount = reward?.discount && reward.discount > 0;
  const tokenAmount = reward?.variants?.[0]?.value || reward?.startingTokenCost || 0;
  const discount = reward?.discount ?? 0;
  const isFavorited = favoritedItems.some(item => item.id === reward?.id);
  return <Box backgroundColor={hasDiscount ? 'kirby.300' : 'white'} borderRadius={18} boxShadow="standardCard" data-testid="reward-card" display="flex" // Ensures the card content uses flexbox
  flexDirection="column" // Stacks elements vertically
  height="100%" // Ensures consistent card height
  justifyContent="space-between" // Pushes content to fill space
  maxWidth="400px" onClick={() => handleRewardClick(reward as RewardCard)} overflow="hidden" position="relative" {...props} data-sentry-element="Box" data-sentry-component="CbRewardCard" data-sentry-source-file="CbRewardCard.tsx">
      {!isPending && hasDiscount && <Badge position="absolute" top={3} right={3} backgroundColor="kirby.400" color="white" padding="4px 8px" borderRadius="full" fontSize="lg" fontWeight="bold" zIndex={1}>
          {`${Math.round(discount * 100)}% OFF`}
        </Badge>}
      <Box position="relative" backgroundColor={hasDiscount ? 'kirby.200' : 'cloud.300'} data-sentry-element="Box" data-sentry-source-file="CbRewardCard.tsx">
        {isPending ? <Skeleton data-testid="skeleton" width="100%" height="100%" paddingBottom="63%" /> : <Image alt={reward?.name} data-testid="image" height="100%" maxHeight="190px" width="100%" objectFit="cover" src={reward?.previewUrl} />}
      </Box>
      <Box padding="18px" flexGrow={1} display="flex" flexDirection="column" data-sentry-element="Box" data-sentry-source-file="CbRewardCard.tsx">
        <Box marginBottom={3} data-sentry-element="Box" data-sentry-source-file="CbRewardCard.tsx">
          {isPending ? <Skeleton height={7} width="200px" /> : <Text color="text.primary" fontFamily="codenext" fontSize={{
          base: 'xl',
          sm: '2xl'
        }} isTruncated>
              {reward?.name}
            </Text>}
        </Box>
        <Flex flexDirection={'row'} alignItems={'center'} justifyContent={'space-between'} marginTop={'auto'} data-sentry-element="Flex" data-sentry-source-file="CbRewardCard.tsx">
          <Flex flexDirection={'row'} data-sentry-element="Flex" data-sentry-source-file="CbRewardCard.tsx">
            <Text color="text.primaryDark" marginRight={2} data-sentry-element="Text" data-sentry-source-file="CbRewardCard.tsx">
              {'From'}
            </Text>
            <Flex alignItems="center" data-sentry-element="Flex" data-sentry-source-file="CbRewardCard.tsx">
              <ArcadeToken marginRight={1} paddingTop="1px" width={['20px', '24px']} height={['20px', '24px']} data-sentry-element="ArcadeToken" data-sentry-source-file="CbRewardCard.tsx" />
              {hasDiscount && <Text textDecoration="line-through" marginRight={2}>
                  {formatNumber(tokenAmount / (1 - discount), false)}
                </Text>}
              <Text color={'text.primaryDark'} fontSize={'md'} fontWeight={'semibold'} data-sentry-element="Text" data-sentry-source-file="CbRewardCard.tsx">
                {formatNumber(tokenAmount, false)}
              </Text>
            </Flex>
          </Flex>
          <Button aria-label={!!reward?.rewardFavouriteId ? 'Unfavorite this item' : 'Favorite this item'} colorScheme="transparent" onClick={e => {
          e.stopPropagation();
          handleFavoriteClick(reward as RewardCard);
        }} pointerEvents={isPending ? 'none' : 'auto'} data-sentry-element="Button" data-sentry-source-file="CbRewardCard.tsx">
            <Box width="28px" height="26px" data-sentry-element="Box" data-sentry-source-file="CbRewardCard.tsx">
              {isFavorited ? <ICON_BY_NAME.heart data-testid="heart-filled" width="30px" height="30px" color="kirby.400" /> : <ICON_BY_NAME.heartoutline color="gray.400" data-testid="heart-outline" width="28px" height="26px" />}
            </Box>
          </Button>
        </Flex>
      </Box>
    </Box>;
};